import type { ChatEvent } from '@/domains/common/api';
import type { ChatAction, ChatMsg } from '@/domains/matilda-agent/chat';
import type { ReactNode } from 'react';

export type ConfirmState = 'idle' | 'busy' | 'done' | 'error';
export type LiveStatusTone = 'thinking' | 'working' | 'waiting';

export interface LiveStatus {
  tone: LiveStatusTone;
  label: string;
}

// Stable id for the ephemeral "working on it" segment, so the first real delta
// can find and drop it.
const ACK_SEGMENT_ID = 'ack-ephemeral';

export interface ActivityRow {
  id: string;
  tool_name: string;
  args: unknown;
  status: 'running' | 'ok' | 'failed';
  subagent_id: string | null;
}

export type TurnSegment =
  | { kind: 'text'; id: string; body: string }
  | { kind: 'activity'; id: string; rows: ActivityRow[] }
  | {
      kind: 'confirm';
      id: string;
      pending_call_id: string;
      tool_name: string;
      summary: string;
      state: ConfirmState;
    };

export interface TurnItem {
  kind: 'turn';
  id: string;
  agent: string;
  time: string;
  segments: TurnSegment[];
  attach: { header: string; rows: [string, string][] } | null;
  chips: ChatAction[];
  streaming: boolean;
  stopped: boolean;
  liveStatus: LiveStatus | null;
}

export type ThreadItem =
  | { kind: 'user'; id: string; time: string; body: string; queued: boolean }
  | TurnItem
  | { kind: 'note'; id: string; agent: string; body: ReactNode; tag: string | null };

export function emptyTurn(id: string, agent: string): TurnItem {
  return {
    kind: 'turn',
    id,
    agent,
    time: 'just now',
    segments: [],
    attach: null,
    chips: [],
    streaming: true,
    stopped: false,
    liveStatus: { tone: 'thinking', label: 'Thinking...' },
  };
}

export function applyChatEvent(turn: TurnItem, event: ChatEvent, newId: () => string): TurnItem {
  // Ephemeral acknowledgement — only as the leading element, and dropped the
  // moment real answer text streams in, so it never merges into or persists with
  // the answer.
  if (event.kind === 'ack') {
    if (turn.segments.length > 0) return turn;
    return { ...turn, liveStatus: { tone: 'thinking', label: event.text } };
  }

  if (event.kind === 'delta') {
    const base = turn.segments.some(seg => seg.id === ACK_SEGMENT_ID)
      ? { ...turn, segments: turn.segments.filter(seg => seg.id !== ACK_SEGMENT_ID) }
      : turn;
    return appendText(
      { ...base, liveStatus: { tone: 'thinking', label: 'Writing response...' } },
      event.text,
      newId,
    );
  }

  if (event.kind === 'error') {
    return appendText({ ...turn, liveStatus: null }, `\n\n_error: ${event.message}_`, newId);
  }

  if (event.kind === 'tool_use') {
    const row: ActivityRow = {
      id: event.tool_use_id,
      tool_name: event.name,
      args: event.args,
      status: 'running',
      subagent_id: null,
    };
    const last = turn.segments[turn.segments.length - 1];
    if (last?.kind === 'activity') {
      return {
        ...replaceSegment(turn, last.id, { ...last, rows: [...last.rows, row] }),
        liveStatus: { tone: 'working', label: 'Waiting for tool result...' },
      };
    }
    return {
      ...turn,
      segments: [...turn.segments, { kind: 'activity', id: newId(), rows: [row] }],
      liveStatus: { tone: 'working', label: 'Waiting for tool result...' },
    };
  }

  if (event.kind === 'tool_result') {
    return {
      ...turn,
      segments: turn.segments.map(seg =>
        seg.kind !== 'activity'
          ? seg
          : {
              ...seg,
              rows: seg.rows.map(row =>
                row.id !== event.tool_use_id
                  ? row
                  : {
                      ...row,
                      status: event.ok !== false ? 'ok' : 'failed',
                      subagent_id: event.meta?.subagent_id ?? null,
                    },
              ),
            },
      ),
      liveStatus: event.meta?.subagent_id
        ? { tone: 'waiting', label: 'Waiting for sub-agent to report back...' }
        : { tone: 'thinking', label: 'Thinking over the result...' },
    };
  }

  if (event.kind === 'pending_confirm') {
    const segment: TurnSegment = {
      kind: 'confirm',
      id: newId(),
      pending_call_id: event.pending_call_id,
      tool_name: event.tool_name,
      summary: event.summary,
      state: 'idle',
    };
    return { ...turn, segments: [...turn.segments, segment], liveStatus: null };
  }

  if (event.kind === 'done' || event.kind === 'recommendation') return { ...turn, liveStatus: null };

  return turn;
}

function appendText(turn: TurnItem, text: string, newId: () => string): TurnItem {
  const last = turn.segments[turn.segments.length - 1];
  if (last?.kind === 'text') return replaceSegment(turn, last.id, { ...last, body: last.body + text });
  return { ...turn, segments: [...turn.segments, { kind: 'text', id: newId(), body: text }] };
}

function replaceSegment(turn: TurnItem, segmentId: string, next: TurnSegment): TurnItem {
  return { ...turn, segments: turn.segments.map(seg => (seg.id === segmentId ? next : seg)) };
}

export function turnHasContent(turn: TurnItem): boolean {
  return (
    turn.segments.filter(
      seg => seg.id !== ACK_SEGMENT_ID && (seg.kind !== 'text' || seg.body.trim().length > 0),
    ).length > 0 ||
    turn.chips.length > 0 ||
    turn.attach !== null
  );
}

export function threadItemsFromChatMsgs(msgs: ChatMsg[]): ThreadItem[] {
  return msgs.map((msg, i) => {
    const id = `seed-${i}`;
    if (msg.kind === 'user') return { kind: 'user', id, time: msg.time, body: msg.body, queued: false };
    if (typeof msg.body !== 'string') {
      return { kind: 'note', id, agent: msg.agent, body: msg.body, tag: null };
    }
    const actions = msg.actions ?? [];
    const segments: TurnSegment[] = [
      ...(msg.body.trim().length > 0 ? [{ kind: 'text' as const, id: `${id}-text`, body: msg.body }] : []),
      ...actions.flatMap((action, j): TurnSegment[] => {
        if (action.kind === 'tool_activity') {
          return [
            {
              kind: 'activity',
              id: `${id}-act-${j}`,
              rows: action.items.map((item, k) => ({
                id: `${id}-act-${j}-${k}`,
                tool_name: item.tool_name,
                args: undefined,
                status: item.ok ? 'ok' : 'failed',
                subagent_id: item.subagent_id,
              })),
            },
          ];
        }
        if (action.kind === 'tool_call') {
          return [
            {
              kind: 'confirm',
              id: `${id}-confirm-${j}`,
              pending_call_id: action.pending_call_id,
              tool_name: action.tool_name,
              summary: action.summary,
              state: 'idle',
            },
          ];
        }
        return [];
      }),
    ];
    return {
      kind: 'turn',
      id,
      agent: msg.agent,
      time: msg.time,
      segments,
      attach: msg.attach ?? null,
      chips: actions.filter(action => action.kind !== 'tool_call' && action.kind !== 'tool_activity'),
      streaming: false,
      stopped: false,
      liveStatus: null,
    };
  });
}
